import { z } from "zod";

import {
  assertDocumentCustomTextBoilerplateEditorSettings,
  DocumentCustomTextBoilerplateSchema,
  DocumentCustomTextModalSlotsSchema,
  emptyDocumentCustomTextBoilerplate,
  emptyDocumentCustomTextModalSlots,
} from "~/shared/documents/schemas/document-custom-text.schema";
import { DisplayUomOverrideSchema } from "~/shared/documents/schemas/document-display-uom.schema";
import { EditorSchema } from "~/utils/types/rich-text";

/** Org key WAREHOUSE_RELEASE_DOCUMENT_CONFIG — SO Warehouse Release v3 PDF (SC-2489). */
export const WAREHOUSE_RELEASE_DOCUMENT_CONFIG_VERSION = 1;

/** Per-create modal overrides — rich text (TipTap), not persisted on the order. */
export const WarehouseReleasePdfInputSchema = z.object({
  customTextHeader: EditorSchema.optional(),
  customTextFooter: EditorSchema.optional(),
  displayUomOverride: DisplayUomOverrideSchema,
});

export type WarehouseReleasePdfInput = z.infer<typeof WarehouseReleasePdfInputSchema>;

export const WarehouseReleaseDocumentConfigSchema = z.object({
  configVersion: z.literal(WAREHOUSE_RELEASE_DOCUMENT_CONFIG_VERSION).default(WAREHOUSE_RELEASE_DOCUMENT_CONFIG_VERSION),
  boilerplate: DocumentCustomTextBoilerplateSchema.default(emptyDocumentCustomTextBoilerplate()),
  modalSlots: DocumentCustomTextModalSlotsSchema.default(emptyDocumentCustomTextModalSlots()),
});

export type WarehouseReleaseDocumentConfig = z.infer<typeof WarehouseReleaseDocumentConfigSchema>;

export const emptyWarehouseReleaseDocumentConfig = (): WarehouseReleaseDocumentConfig => ({
  configVersion: WAREHOUSE_RELEASE_DOCUMENT_CONFIG_VERSION,
  boilerplate: emptyDocumentCustomTextBoilerplate(),
  modalSlots: emptyDocumentCustomTextModalSlots(),
});

export const parseWarehouseReleaseDocumentConfig = (raw: unknown): WarehouseReleaseDocumentConfig =>
  WarehouseReleaseDocumentConfigSchema.parse(raw);

/** Parse stored JSON or return null when corrupt (PDF fail-soft). */
export const tryParseWarehouseReleaseDocumentConfigString = (raw: string): WarehouseReleaseDocumentConfig | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = WarehouseReleaseDocumentConfigSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
};

export class WarehouseReleaseDocumentConfigParseError extends Error {
  constructor(message = "Warehouse release document settings could not be read. Contact support before saving.") {
    super(message);
    this.name = "WarehouseReleaseDocumentConfigParseError";
  }
}

/** Settings reads — fail-hard when stored JSON is present but invalid. */
export const parseWarehouseReleaseDocumentConfigForSettings = (raw: string | null | undefined): WarehouseReleaseDocumentConfig => {
  if (!raw?.trim()) {
    return emptyWarehouseReleaseDocumentConfig();
  }
  const parsed = tryParseWarehouseReleaseDocumentConfigString(raw);
  if (!parsed) {
    throw new WarehouseReleaseDocumentConfigParseError();
  }
  return parsed;
};

/** Super-admin org setting overwrite for WAREHOUSE_RELEASE_DOCUMENT_CONFIG. */
export const assertWarehouseReleaseDocumentOrgSettingValueForWrite = (value: string): void => {
  if (!value?.trim()) {
    return;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error("Invalid warehouse release document config JSON");
  }
  const result = WarehouseReleaseDocumentConfigSchema.safeParse(parsed);
  if (!result.success) {
    throw new Error("Invalid warehouse release document config schema");
  }
  assertDocumentCustomTextBoilerplateEditorSettings(result.data.boilerplate);
};

export const serializeWarehouseReleaseDocumentConfig = (config: WarehouseReleaseDocumentConfig): string =>
  JSON.stringify(WarehouseReleaseDocumentConfigSchema.parse(config));
